import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { FLAG_COMPANIES_WEBCHAT } from "@bo/shared";
import { api } from "@/lib/api.js";
import { useAuthStore } from "@/lib/auth.js";
import { claves, webchatsApi } from "@/lib/webchat/api.js";
import { interpretarError } from "@/lib/webchat/errores.js";
import { ESTADO_WEBCHAT } from "@/lib/webchat/estado.js";
import { AvisoError } from "./Avisos.js";
import { Modal } from "./Dialogos.js";
import { StatusDot } from "./StatusDot.js";

interface Flag {
  id: number;
  flagKey: string;
  overrides: { idcompany: number; enabled: boolean; note: string | null }[];
}

function DialogoHabilitar({
  habilitar,
  ocupado,
  error,
  onCerrar,
  onConfirmar,
}: Readonly<{
  habilitar: boolean;
  ocupado: boolean;
  error: unknown;
  onCerrar: () => void;
  onConfirmar: (nota: string) => void;
}>) {
  const [nota, setNota] = useState("");
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={habilitar ? "Marcar como atendida por el social de ISOFT" : "Quitar del social de ISOFT"}
      descripcion={
        habilitar
          ? "Confirmá que los asesores de esta company trabajan en el social de ISOFT (social-isoft). Si atienden en otra instancia, la derivación de sus webchats no les llega."
          : "Sus webchats dejan de verse y editarse en este módulo. No se toca ningún webchat."
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirmar(nota.trim());
        }}
      >
        <label className="block space-y-1">
          <span className="text-sm font-medium">Nota (opcional)</span>
          <input className="input" maxLength={255} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>
        {error ? <AvisoError error={interpretarError(error)} /> : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={ocupado}>
            Confirmar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * La sección «Webchats» de la ficha de una company: si está atendida por el
 * social de ISOFT (override del flag webchat_social_isoft), sus webchats y el
 * acceso al alta; quien administra los flags puede habilitarla o quitarla.
 */
export function WebchatsDeCompany({ idcompany }: Readonly<{ idcompany: number }>) {
  const qc = useQueryClient();
  const puedeLeer = useAuthStore((s) => s.hasPermission("webchat.read"));
  const puedeCrear = useAuthStore((s) => s.hasPermission("webchat.write"));
  const administraFlags = useAuthStore((s) => s.hasPermission("feature_flag.write"));
  const [dialogo, setDialogo] = useState(false);
  const flags = useQuery({ queryKey: ["feature-flags"], queryFn: () => api.get<{ items: Flag[] }>("/feature-flags"), enabled: puedeLeer || administraFlags });
  const flag = flags.data?.items.find((f) => f.flagKey === FLAG_COMPANIES_WEBCHAT);
  const habilitada = flag?.overrides.some((o) => o.idcompany === idcompany && o.enabled) ?? false;
  const webchats = useQuery({
    queryKey: claves.listado({ idcompany, page: 1, pageSize: 50 }),
    queryFn: () => webchatsApi.listar({ idcompany, page: 1, pageSize: 50 }),
    enabled: puedeLeer && habilitada,
  });
  const cambiar = useMutation({
    mutationFn: (nota: string) =>
      api.put(`/feature-flags/${flag?.id ?? 0}/override`, { idcompany, enabled: !habilitada, ...(nota ? { note: nota } : {}) }),
    onSuccess: () => {
      toast.success(habilitada ? "La company ya no figura en el social de ISOFT" : "Company habilitada para Webchats");
      setDialogo(false);
      void qc.invalidateQueries({ queryKey: ["feature-flags"] });
      void qc.invalidateQueries({ queryKey: claves.raiz });
    },
  });

  if (!puedeLeer && !administraFlags) return null;
  return (
    <section className="card relative" aria-labelledby="titulo-webchats-company">
      <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="titulo-webchats-company" className="font-semibold inline-flex items-center gap-2">
            <MessageSquare size={16} aria-hidden="true" /> Webchats
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            <StatusDot
              tono={habilitada ? "ok" : "neutro"}
              etiqueta={habilitada ? "Atendida por el social de ISOFT" : "No figura como atendida por el social de ISOFT"}
              className="text-xs"
            />
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {administraFlags && flag && (
            <button type="button" className="btn-secondary text-sm" onClick={() => setDialogo(true)}>
              {habilitada ? "Quitar del social de ISOFT" : "Marcar como atendida por el social de ISOFT"}
            </button>
          )}
          {puedeCrear && habilitada && (
            <Link to={`/webchats/nuevo?idcompany=${idcompany}`} className="btn-primary text-sm inline-flex items-center gap-1">
              <Plus size={14} aria-hidden="true" /> Nuevo webchat
            </Link>
          )}
        </div>
      </header>
      <div className="p-5">
        {flags.isLoading && <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Cargando" />}
        {!habilitada && !flags.isLoading && (
          <p className="text-sm text-slate-500">El módulo Webchats sólo administra companies atendidas por el social de ISOFT.</p>
        )}
        {habilitada && puedeLeer && webchats.data && webchats.data.items.length === 0 && (
          <p className="text-sm text-slate-500">Esta company todavía no tiene webchats.</p>
        )}
        {habilitada && puedeLeer && (webchats.data?.items.length ?? 0) > 0 && (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {webchats.data?.items.map((w) => {
              const e = ESTADO_WEBCHAT[w.estado];
              const destino = w.idcanal ? `/webchats/${w.idcanal}` : `/webchats/operaciones/${w.operacionId ?? 0}`;
              return (
                <li key={`${w.idcanal ?? "op"}-${w.operacionId ?? w.host}`} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <Link to={destino} className="mono text-[13px] font-medium hover:underline">
                    {w.host}
                  </Link>
                  <span className="text-slate-500">{w.nombre ?? ""}</span>
                  <StatusDot tono={e.tono} etiqueta={e.etiqueta} className="ml-auto" />
                </li>
              );
            })}
          </ul>
        )}
        {webchats.error && <AvisoError error={interpretarError(webchats.error)} />}
      </div>
      {dialogo && (
        <DialogoHabilitar
          habilitar={!habilitada}
          ocupado={cambiar.isPending}
          error={cambiar.error}
          onCerrar={() => setDialogo(false)}
          onConfirmar={(nota) => cambiar.mutate(nota)}
        />
      )}
    </section>
  );
}
